import React, { useEffect, useRef, useState } from 'react';
import { formatDate, summarizePauseHistory } from '../utils/membershipFees';
import './PauseHistoryBadge.css';

// Badge shown on a member profile when the membership is (or has been) paused.
// Hovering previews the history, clicking pins it open.
function PauseHistoryBadge({ member }) {
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);

  const { history, isPaused, hasBeenPaused, totalPauses, totalPausedDays, activePause } =
    summarizePauseHistory(member);

  // Close the pinned panel when clicking elsewhere
  useEffect(() => {
    if (!isPinned) return undefined;

    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsPinned(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isPinned]);

  if (!hasBeenPaused) return null;

  const isOpen = isPinned || isHovered;
  const badgeLabel = isPaused
    ? 'Currently Paused'
    : `Paused Before${totalPauses > 1 ? ` (${totalPauses}×)` : ''}`;

  return (
    <div
      className="pause-history-badge-container"
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        className={`pause-history-badge ${isPaused ? 'is-paused' : 'was-paused'}`}
        onClick={() => setIsPinned((prev) => !prev)}
        aria-expanded={isOpen}
        title={
          isPaused
            ? `Membership paused since ${formatDate(activePause?.pauseDate)}`
            : `Membership has been paused ${totalPauses} time${totalPauses === 1 ? '' : 's'}`
        }
      >
        <span className="pause-history-badge-icon">⏸️</span>
        <span className="pause-history-badge-label">{badgeLabel}</span>
      </button>

      {isOpen && (
        <div className="pause-history-panel" role="dialog" aria-label="Pause and resume history">
          <div className="pause-history-panel-header">
            <h4>Pause &amp; Resume History</h4>
            <span className="pause-history-summary">
              {totalPauses} pause{totalPauses === 1 ? '' : 's'} · {totalPausedDays} day
              {totalPausedDays === 1 ? '' : 's'} total
            </span>
          </div>

          <ul className="pause-history-list">
            {history.map((entry) => (
              <li
                key={entry.id}
                className={`pause-history-item ${entry.status === 'active' ? 'ongoing' : 'completed'}`}
              >
                <div className="pause-history-item-dates">
                  <span className="pause-history-date paused">⏸️ {formatDate(entry.pauseDate)}</span>
                  <span className="pause-history-arrow">→</span>
                  <span className="pause-history-date resumed">
                    {entry.resumeDate ? `▶️ ${formatDate(entry.resumeDate)}` : 'Ongoing'}
                  </span>
                </div>
                <div className="pause-history-item-meta">
                  <span className="pause-history-duration">
                    {entry.durationDays} day{entry.durationDays === 1 ? '' : 's'}
                    {entry.status === 'active' ? ' so far' : ''}
                  </span>
                  <span className="pause-history-reason" title={entry.reason}>
                    {entry.reason}
                  </span>
                </div>
                {entry.pausedBy && (
                  <div className="pause-history-actor">
                    Paused by {entry.pausedBy}
                    {entry.resumedBy ? ` · Resumed by ${entry.resumedBy}` : ''}
                  </div>
                )}
              </li>
            ))}
          </ul>

          <p className="pause-history-note">
            No fees accrue between a pause date and its resume date.
          </p>
        </div>
      )}
    </div>
  );
}

export default PauseHistoryBadge;
